/**
 * Single global TanStack Query client.
 *
 * Used by every server-state hook (`useDevices`, `useRoutines`,
 * `useSuggestions`, `useSettings`, etc.). Mounted into the React tree
 * by `<App />` via `<QueryClientProvider client={queryClient}>`.
 *
 * Defaults rationale:
 *  - `staleTime: 30s` — most reads tolerate short staleness; per-hook
 *    overrides bump this (suggestions = 60s, routines = 5min).
 *  - `gcTime: 5min` — cached data sticks around long enough that a
 *    user navigating back to /dashboard after visiting /settings sees
 *    instant content while the background refetch happens.
 *  - `refetchOnWindowFocus: true` — desktop users tab away + come back;
 *    refresh feels snappy.
 *  - `refetchOnReconnect: true` — pair with the offline detection
 *    (`useOfflineDetection` from T-030, future).
 *  - `retry: 1` — network blips are common in a home environment; one
 *    silent retry is fine. Pre-mutation failures don't retry — the user
 *    gets immediate feedback via toast (T-011).
 *  - `throwOnError` left at the per-query default (`false`); component-
 *    level error states render the toast/banner.
 */
import { QueryClient } from "@tanstack/react-query";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 300_000,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      retry: 1,
    },
    mutations: {
      retry: 0,
    },
  },
});
